import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Sparkles } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import StarRating from "../../components/StarRating";
import { getSkills, getMySkills, upsertMySkills } from "../../services/archMarketplace";

const FAMILIES = [
  { key: "SOFTWARE", label: "Software" },
  { key: "ARCHITECTURE", label: "Architecture Skills" },
];

function FreelancerSkills() {
  const [catalog, setCatalog] = useState([]);
  const [ratings, setRatings] = useState({}); // skillId -> stars
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [all, mine] = await Promise.all([getSkills(), getMySkills().catch(() => ({ skills: [] }))]);
        if (!mounted) return;
        setCatalog(Array.isArray(all) ? all : []);
        const map = {};
        (mine?.skills || []).forEach((s) => {
          map[s.skillId] = s.stars;
        });
        setRatings(map);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const byFamily = useMemo(() => {
    const g = { SOFTWARE: [], ARCHITECTURE: [] };
    catalog.forEach((s) => g[s.family]?.push(s));
    return g;
  }, [catalog]);

  const setStars = (skillId, stars) => setRatings((r) => ({ ...r, [skillId]: stars }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(ratings)
        .filter(([, stars]) => stars > 0)
        .map(([skillId, stars]) => ({ skillId: Number(skillId), stars }));
      await upsertMySkills(payload);
      toast.success("Skills saved. You're now matchable for these skills.");
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Could not save skills.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Loading…</div>;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="My Skills"
        subtitle="Rate yourself honestly — projects are matched to your strongest skills"
        icon={Sparkles}
        backTo="/freelancer/dashboard"
        backLabel="Back"
        actions={
          <button
            onClick={save}
            disabled={saving}
            className="rounded-md bg-white/95 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-white disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Skills"}
          </button>
        }
      />

      {FAMILIES.map((fam) => (
        <div key={fam.key} className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">{fam.label}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {(byFamily[fam.key] || []).map((s) => (
              <div key={s.skillId} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{s.name}</span>
                <StarRating value={ratings[s.skillId] || 0} onChange={(v) => setStars(s.skillId, v)} size={20} />
              </div>
            ))}
          </div>
        </div>
      ))}

      <button
        onClick={save}
        disabled={saving}
        className="rounded-md bg-flamingo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-flamingo-700 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Skills"}
      </button>
    </div>
  );
}

export default FreelancerSkills;
